import type { SalesRep } from "@shared/types/SalesRep";

export interface Distributor {
  account_number?: string | null;
  address1?: string | null;
  allow_manual_entry?: boolean;
  allow_ordering_out_of_stock: boolean;
  allow_retailer_deals?: boolean;
  always_choose_distributor?: boolean;
  brandless?: boolean;
  city?: string;
  cloudinary_image_url?: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  custom_price_counts?: number | null;
  customer_service_rep_id?: number | null;
  cutoff_time?: string | null;
  default_sales_rep?: SalesRep | null;
  distributor_account_id?: number | null;
  email_domains?: DistributorEmailDomainMatch[];
  enforce_allocation_amounts?: boolean | null;
  external_id?: string | null;
  has_storefront?: boolean;
  hide_pricing?: boolean;
  id: number;
  image_upload_id?: number;
  integrated_sales_reps?: boolean;
  integrated?: boolean;
  is_rolling_out_integrated_orders?: boolean | null;
  manual_order_action_required?: boolean;
  minimum_dollars_per_order?: number | null;
  minimum_quantity_per_order?: string | null;
  modifiable_account_numbers?: boolean;
  monday_delivery_on_weekend_orders?: boolean;
  name: string;
  next_cutoff?: string | null;
  no_sales_reps_needed?: boolean;
  ordering_disabled?: boolean;
  ordering_notes?: string | null;
  require_account_number_for_add_to_cart?: boolean | null;
  requires_account_number?: boolean | null;
  require_retailer_user_authentication?: boolean | null;
  retailer_direct_add?: boolean | null;
  retailer_distributor_id?: number | null;
  retailer_facing_name?: string | null;
  retailers_can_see_unassigned_inventory?: boolean;
  sales_reps?: SalesRep[];
  self_managed_inventory?: string | null;
  seven_fifty_integration?: boolean | null;
  sf_requires_a_price_to_order_a_product?: boolean | null;
  sf_vendor_id?: number;
  show_discount_schedule?: boolean;
  show_out_of_stock_msg?: boolean;
  sms_only_brandless?: boolean | null;
  state?: string | null;
  timezone?: string | null;
  uneditable_sellsheet_products?: boolean;
  unused?: boolean;
  uses_integrated_regions?: boolean;
  uses_multiple_skus?: boolean;
  uses_price_zones_for_discounts?: boolean;
  uses_price_zones?: boolean;
  uses_sell_sheets?: boolean;
  uses_storefronts?: boolean;
  uses_term_payments?: boolean;
  uses_warehouse_pickup?: boolean;
  uses_warehouses?: boolean;
  zip?: string | null;
  retailerUserAuthenticationStatus?: string;
  uses_wine_cellar: boolean;
  default_stock_site_id?: string | null;
  default_warehouse_id?: number | null;
  fulfillment_model:
    | "delivery_only"
    | "pickup_only"
    | "pickup_and_delivery"
    | "delivery_and_single_pickup_location";
}

export interface DistributorInfo {
  allow_ordering_out_of_stock: boolean;
  associated: boolean;
  distributor_id: number;
  distributor_name: string;
  enforce_allocation_amounts: boolean;
  id?: number;
  integrated: boolean;
  is_vintage_product: boolean;
  name?: string;
  ordering_disabled?: boolean;
  requires_account_number_for_add_to_cart: boolean;
  sevenfifty_integrated: boolean;
  uses_warehouse_pickup: boolean;
  warehouse: boolean;
  uses_wine_cellar: boolean;
  requires_account_number: boolean;
  fulfillment_model:
    | "delivery_only"
    | "pickup_only"
    | "pickup_and_delivery"
    | "delivery_and_single_pickup_location";
  account_number: string;
  default_stock_site_id: string;
}

export interface DualProductDistributor {
  id: number;
  integrated: boolean;
  name: string;
  ordering_disabled?: boolean;
  seven_fifty_integration: boolean;
  uses_price_zones: boolean;
  uses_wine_cellar: boolean;
  account_number: string;
}

export interface DistributorConnectedReasons {
  account: boolean;
  connected_account_inactive: boolean;
  has_reached_daily_order_minimum: boolean;
  needs_to_reach_order_minimum: boolean;
  no_rep_integration: boolean;
  rep: boolean;
  requires_match_confirmation: boolean;
}

export interface DistributorCart {
  account_number: string;
  allow_ordering_out_of_stock: boolean;
  allow_retailer_deals: boolean;
  associated?: boolean;
  available: { [key: string]: boolean };
  cloudinary_image_url?: string;
  connected?: boolean;
  disable_below_order_minimum_submit: boolean;
  distributor_account_id: number | null;
  id: number;
  integrated_custom_order_blocking?: boolean;
  integrated: boolean;
  is_rolling_out_integrated_orders: boolean;
  minimum_dollars_per_order_off_premise?: number;
  minimum_dollars_per_order: number;
  minimum_quantity_per_order_off_premise?: string;
  minimum_quantity_per_order: string;
  modifiable_account_numbers: boolean;
  name: string;
  next_cutoff?: string | null;
  no_rep_notifications?: boolean;
  ordering_disabled?: boolean;
  ordering_notes: string | null;
  reasons?: DistributorConnectedReasons;
  require_account_number_for_add_to_cart?: boolean;
  requires_account_number: boolean;
  retailer_distributor_id: number;
  retailer_facing_name?: string | null;
  seven_fifty_integration: boolean;
  sf_uses_cash_on_delivery: boolean;
  sf_uses_split_invoices: boolean;
  sf_vendor_id?: number;
  tabc_delivery_minimum_order_blocking?: boolean;
  uses_integrated_delivery_dates: boolean;
  uses_price_zones_for_discounts: boolean;
  uses_price_zones: boolean;
  uses_warehouse_pickup?: boolean;
  uses_wine_cellar: boolean;
  uses_happy_hour: boolean;
  speakeasy_error?: string;
  default_stock_site_id?: string | null;
  texas_partner: boolean;
  fulfillment_model:
    | "delivery_only"
    | "pickup_only"
    | "pickup_and_delivery"
    | "delivery_and_single_pickup_location";
  brandless?: boolean;
}

export interface DistributorAccountConfig {
  delivery_day_of_week?: string | null;
  location_code?: string | null;
  sales_reps?: string[];
  should_block_orders?: boolean | null;
}

export interface DistributorAccountSalesRep {
  automatic_delete: boolean;
  contact_email: string | null;
  contact_phone: string | null;
  deleted_at: string | null;
  do_not_contact: boolean;
  id: number;
  integrated: boolean;
  integration_id: number | null;
  name: string;
  sanitized_phone: string | null;
  user_id: number | null;
}

export interface DistributorAccount {
  account_number: number;
  address1: string;
  city: string;
  distributor_account_skus: string[];
  distributor: Distributor;
  id: number;
  liquor_license?: string | null;
  name: string;
  off_premises?: boolean | null;
  retailer_user?: any;
  retailers: any[];
  sales_reps: DistributorAccountSalesRep[];
  state: string;
  zip: string;
}

export interface DistributorSimple {
  address1?: string;
  city: string;
  id: number;
  integrated?: boolean;
  is_rolling_out_integrated_orders?: boolean;
  name: string;
  state: string;
  users_warehouses?: boolean;
}

export interface DistributorUser {
  cloudinary_image_url: string;
  distributor_id: number;
  email: string;
  first_name: string;
  has_seen_first_storefront_edit_modal?: boolean;
  hide_referral_incentive: boolean;
  id?: number;
  image_upload_id?: number;
  integrated: boolean;
  last_name: string;
  limited_ordering?: boolean;
  market?: any;
  name: string;
  no_sales_rep_needed: boolean;
  phone: string;
  role: string;
  sales_rep_id: number;
  self_managed_inventory: boolean;
  state: string;
  uneditable_sellsheet_products: boolean;
  uses_referrals: string;
  uses_sell_sheets: boolean;
  uses_storefronts: boolean;
  uses_term_payments: boolean;
  group_director: boolean;
}

export enum DistributorEmployeeRole {
  DistributorAdmin = "distributor_admin",
  Manager = "manager",
  PaymentsAdmin = "payments_admin",
  SalesRep = "sales_rep",
}

export interface DistributorEmployee {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: DistributorEmployeeRole;
}

export interface DistributorEmailDomainMatch {
  distributor_id: number;
  domain: string;
  id: number;
}

export interface DistributorFilterData {
  checked: boolean;
  id: number;
  name: string;
}

export interface DefaultDistributors {
  [product_type_id: number]: {
    defaulted_distributor: number | null;
    distributor_options: DistributorSimple[];
  };
}

export interface DistributorOrderCounts {
  [distributorId: number]: number;
}

export interface DistributorVacation {
  assigned_email?: string | null;
  assigned_sales_rep_id?: number | null;
  assigned_sales_rep?: SalesRep | null;
  end_time?: any | null;
  id?: number;
  keep_notifying?: boolean | null;
  no_end_time?: boolean | null;
  sales_rep?: SalesRep | null;
  start_time?: any | null;
  user_id?: number | null;
}

export type SellSheetDistributor = {
  brandless: boolean;
  business_name: string;
  cloudinary_image_url: string;
  id: number;
  name: string;
  ordering_disabled: boolean;
  seven_fifty_integration: boolean;
  uses_warehouse_pickup: boolean;
};
